// Date utility functions for diary management

/**
 * Get the start of the week (Monday) for a given date
 */
export const getStartOfWeek = (date: Date): Date => {
  const result = new Date(date);
  const day = result.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  result.setDate(result.getDate() + diff);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of the week (Friday) for a given date
 */
export const getEndOfWeek = (date: Date): Date => {
  const result = getStartOfWeek(date);
  result.setDate(result.getDate() + 4);
  result.setHours(23, 59, 59, 999);
  return result;
};

export const formatToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format date for display (e.g., "20 Oct - 25 Oct 2025")
 */
export const formatWeekRange = (startDate: Date, endDate: Date): string => {
  const options: Intl.DateTimeFormatOptions = { 
    day: '2-digit', 
    month: 'short' 
  };
  
  const start = startDate.toLocaleDateString('en-GB', options);
  const end = endDate.toLocaleDateString('en-GB', options);
  const year = endDate.getFullYear();
  
  return `${start} - ${end} ${year}`;
};

/**
 * Check if a week is the current work week
 */
export const isCurrentWorkWeek = (weekStart: Date): boolean => {
  const currentWeek = getCurrentWorkWeek();
  return weekStart.getTime() === currentWeek.start.getTime();
};

/**
 * Get the current work week boundaries (Monday to Friday)
 * If today is Monday-Friday, returns this week
 * If today is Saturday/Sunday, returns next week
 */
export const getCurrentWorkWeek = (): { start: Date; end: Date } => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
  
  // If weekend, get next week's Monday-Friday
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    const daysUntilMonday = dayOfWeek === 0 ? 1 : 2; // Sunday -> 1, Saturday -> 2
    const nextMonday = new Date(now);
    nextMonday.setDate(now.getDate() + daysUntilMonday);
    const start = getStartOfWeek(nextMonday);
    const end = getEndOfWeek(nextMonday);
    return { start, end };
  }
  
  // Otherwise return current week (Monday-Friday)
  const start = getStartOfWeek(now);
  const end = getEndOfWeek(now);
  return { start, end };
};

/**
 * Navigate to the next work week
 */
export const getNextWorkWeek = (currentStart: Date): { start: Date; end: Date } => {
  const nextMonday = new Date(currentStart);
  nextMonday.setDate(nextMonday.getDate() + 7);
  return {
    start: nextMonday,
    end: getEndOfWeek(nextMonday),
  };
};

/**
 * Navigate to the previous work week
 */
export const getPreviousWorkWeek = (currentStart: Date): { start: Date; end: Date } => {
  const previousMonday = new Date(currentStart);
  previousMonday.setDate(previousMonday.getDate() - 7);
  return {
    start: previousMonday,
    end: getEndOfWeek(previousMonday),
  };
};
